import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Typography, Grid, Card, CardContent, Avatar, Chip, IconButton } from '@mui/material';
import CallToAction from '../widgets/CallToAction';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

export default function APropos() {
    const team = [
        {
            name: "Jérôme de Servigny",
            role: "Président & Co-fondateur",
            description: "Ingénieur Arts et Métiers ParisTech",
            avatar: "JS",
            photo: "/assets/images/jerome.jpg",
            linkedin: "https://www.linkedin.com/in/jerome-de-servigny-004252228/"
        },
        {
            name: "Jean de Rosnay",
            role: "Directeur Général & Co-fondateur",
            description: "Ingénieur ISEP",
            avatar: "JR",
            photo: "/assets/images/jean.jpg",
            linkedin: "https://www.linkedin.com/in/jean-dulong-de-rosnay-241540209/"
        },
        {
            name: "Manthan Bhatt",
            role: "Software Developer",
            description: "Ingénieur EPITA",
            avatar: "MB",
            photo: "/assets/images/manthan.png",
            linkedin: "https://www.linkedin.com/in/itsmanthanbhatt/"
        }
    ];

    const values = [
        {
            title: "Innovation",
            description: "Nous révolutionnons la recherche d'emploi grâce à l'intelligence artificielle"
        },
        {
            title: "Égalité des chances",
            description: "Nous croyons que chaque étudiant mérite d'accéder aux meilleures opportunités"
        },
        {
            title: "Rapidité",
            description: "Nous visons à permettre les meilleures embauches dans un temps record"
        },
        {
            title: "Transparence",
            description: "Nous cultivons la confiance par la transparence et l'honnêteté"
        }
    ];

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Helmet>
                <title>À propos — Student Link</title>
                <meta name="description" content="Notre mission: mettre l'humain au centre du recrutement étudiant grâce à l'IA. Découvrez l'équipe et nos valeurs." />
                <link rel="canonical" href="https://studentlink.fr/a-propos" />
            </Helmet>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
                <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    À propos de Student Link
                </Typography>
                <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}>
                    Notre mission : mettre l'humain au centre du recrutement étudiant grâce à l'IA
                </Typography>
            </Box>

            <Grid container spacing={6} sx={{ mb: 8 }} alignItems="center">
                <Grid item xs={12} md={6}>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        Changer la recherche d'emploi
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                        Chez Student Link, notre pari est de fluidifier massivement le recrutement en connectant rapidement les bons talents aux bons postes. Nous offrons une expérience candidat agréable et innovante, au cœur d’un écosystème unique réunissant étudiants, écoles et entreprises.

                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                        Conçues par deux jeunes ingénieurs passionnés et fraîchement diplômés nos outils ont été taillés pour répondre aux défis du recrutement étudiant.

                    </Typography>

                </Grid>
                <Grid item xs={12} md={6}>
                    <Box sx={{
                        height: { xs: 340, sm: 380, md: 460 },
                        borderRadius: 8,
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                        overflow: 'hidden'
                    }}>
                        <Box
                            component="img"
                            src="/assets/images/full_team.png"
                            alt="Jean & Jérôme - Co-fondateurs Student Link"
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </Box>
                </Grid>
            </Grid>

            <Box sx={{ mb: 8 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 6 }}>
                    Nos Valeurs
                </Typography>
                <Grid container spacing={4}>
                    {values.map((value, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Card sx={{
                                p: 3,
                                height: '100%',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 8,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
                            }}>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#0075FF' }}>
                                        {value.title}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        {value.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Box sx={{ mb: 8 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 6 }}>
                    Notre Équipe
                </Typography>
                <Grid container spacing={4}>
                    {team.map((member, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Card sx={{
                                p: 4,
                                textAlign: 'center',
                                height: '100%',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 8,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
                            }}>
                                <CardContent>
                                    <Avatar
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            mx: 'auto',
                                            mb: 3,
                                            backgroundColor: 'primary.main',
                                            fontSize: '1.5rem'
                                        }}
                                        src={member.photo}
                                        alt={member.name}
                                    >
                                        {member.avatar}
                                    </Avatar>
                                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        {member.name}
                                    </Typography>
                                    <Chip label={member.role} color="primary" variant="outlined" sx={{ mb: 2 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                        <IconButton
                                            component="a"
                                            href={member.linkedin}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`LinkedIn de ${member.name}`}
                                            sx={{ color: 'primary.main' }}
                                        >
                                            <LinkedInIcon />
                                        </IconButton>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        {member.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <CallToAction
                title="Rejoignez l'aventure Student Link"
                subtitle={"Nous recrutons ! Si vous partagez notre vision et souhaitez révolutionner l'avenir professionnel des étudiants, contactez-nous."}
                chips={[
                    { label: '2 postes ouverts', color: 'primary' },
                    { label: 'IA & Data', variant: 'outlined' },
                    { label: 'Marketing Digital', variant: 'outlined' }
                ]}
            />
        </Container>
    );
}